import React, { useState } from 'react';
import CommonContainer from '../../common/CommonContainer';
import TransactionsForm from './TransactionsForm';
import RegularDataGrid from '../../common/table/RegularDataGrid';
import { connect, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { bindActionCreators, compose } from 'redux';
import {
  clearTransactionReports,
  downloadTransaction,
  fetchTransactionReports,
  fetchTransactionReportsOffset,
} from '../../../actions/report-actions';
import { lighten } from '@material-ui/core/styles';
import {
  ALMOST_WHITE_COLOR,
  BLACK_COLOR, BORDER_DARK,
  DARK_UPDATED_FIELD, LIGHT_GREY_COLOR,
  PRIMARY_COLOR,
  VERY_LIGHT_GREY_COLOR,
  WHITE_COLOR,
} from '../../../const/color-const';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import CommonSnackbar from '../../common/CommonSnackbar';
import LoadingSnackbar from '../../common/LoadingSnackbar';
import {
  convertMachineIdToCashRegister,
  getFirstItem,
  getLastItem, getPaymentIdentifiers,
  getPaymentType,
  getTableHeaders,
  isLastPage,
} from './TransactionUtils';
import { DefaultToolbar } from '../reports/standard/StandardReportCustom';
import RenderBreakpoint from '../../common/breakpoints/RenderBreakpoint';
import NoDataAvailableWithIcon from '../../common/charts/NoDataAvailableWithIcon';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Box } from '@material-ui/core';
import PropTypes from 'prop-types';
import { getPaymentTypes } from './TransactionsPaymentCell';

const [DESKTOP_FONT_SIZE, MOBILE_FONT_SIZE] = [14, 12];

const useStyles = makeStyles(theme => ({
  paginationBtn: {
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.7,
    },
  },
  paginationBtnMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: PRIMARY_COLOR,
    padding: theme.spacing(1),
    borderRadius: 4,
    cursor: 'pointer',
    marginLeft: 20,
    '& > span': {
      paddingRight: theme.spacing(1),
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.7,
    },
    '& svg': {
      width: 14,
    },
  },
  transactionsHeader: {
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  noDataFound: {
    marginTop: theme.spacing(4),
    fontSize: 16,
  },
  tooltipContainer: {
    width: '100%',
  },
  itemsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    cursor: 'pointer',
  },
  firstItem: {
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    textOverflow: 'ellipsis',
    textAlign: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
  },
  expandIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: 8,
    height: 22,
    '& > svg': {
      opacity: 0.3,
    },
  },
  documentIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    fontSize: MOBILE_FONT_SIZE,
    left: 50,
    '& > svg': {
      height: 18,
      color: 'grey',
    },
  },
  downloadIcon: {
    cursor: 'pointer',
    color: 'green',
    '& > svg': {
      height: 22,
    },
  },
  tooltipOpen: {
    fontWeight: 'bold',
    '& svg': {
      opacity: 1,
    },
  },
  tooltipTablesWrapper: {
    margin: 0,
    padding: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    background: WHITE_COLOR,
    boxShadow: theme.palette.type === 'light' ? `0 0 20px 0 ${LIGHT_GREY_COLOR}` : `0 0 20px 0 ${BORDER_DARK}`,
    '& thead tr th, tbody tr td': {
      fontSize: DESKTOP_FONT_SIZE,
    },
  },
  tableTitle: {
    width: '100%',
    textAlign: 'center',
    fontSize: DESKTOP_FONT_SIZE,
    fontWeight: 'bold',
    padding: theme.spacing(1),
    margin: theme.spacing(0),
    color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
    background: theme.palette.type === 'light' ? lighten(VERY_LIGHT_GREY_COLOR, 0.6) : DARK_UPDATED_FIELD,
  },
  paginationMobile: {
    fontSize: MOBILE_FONT_SIZE,
    '& .paginationBtn': {
      fontSize: '1.125rem',
    },
    '& svg': {
      fontSize: '1.125rem',
    },
  },
  paginationSummaryMobile: {
    fontSize: MOBILE_FONT_SIZE,
    '& .paginationBtn': {
      fontSize: '1.125rem',
    },
    '& svg': {
      fontSize: '1.125rem',
    },
  },
}));

const Transactions = ({
                        machines,
                        fetchTransactionReports,
                        fetchTransactionReportsOffset,
                        transactions,
                        transactionsTotal,
                        clearTransactionReports,
                      }) => {
  const pageSize = 100;
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [openItem, setOpenItem] = React.useState([]);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [currentPageId, setCurrentPageId] = useState(0);
  const [currentPages, setCurrentPages] = useState([0]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const machinesOptions = machines.map(machine => ({ label: machine.Name, value: machine.Id }));
  const transactionsLength = transactions?.length;
  const initialHeaders = transactionsLength > 0 && Object.keys(transactions[0]);
  const firstItem = getFirstItem(currentPageId, pageSize);
  const lastItem = getLastItem(currentPageId, pageSize, transactionsTotal);
  const currentPageIsLast = isLastPage(currentPageId, pageSize, transactionsTotal);
  const dispatch = useDispatch();
  
  React.useEffect(() => {
    clearTransactionReports();
  }, [clearTransactionReports, formValues]);
  
  const isOpen = (id) => openItem.includes(id);
  
  const getTableData = () => {
    return transactions.map((report, index) => {
      const { TotalAmountIncludingVat, MachineId } = report;
  
      return ({
        ...report, id: index,
        'CashRegister': convertMachineIdToCashRegister(MachineId, machinesOptions),
        'Type': getPaymentType(TotalAmountIncludingVat, formatMessage),
        'Payment': getPaymentTypes(report.Payments, formatMessage),
        'Terminal': getPaymentIdentifiers(report),
      });
    });
  };
  
  const handleDownloadClick = (Number, isInvoice) => {
    dispatch(downloadTransaction(setDownloadProgress, Number, isInvoice));
  };
  
  const headers = transactionsLength > 0 && getTableHeaders(initialHeaders, formValues, setDownloadProgress, openItem, setOpenItem, isOpen, formatMessage, handleDownloadClick);
  
  const paginationSummaryDesktop = () => {
    return <p>Showing {firstItem} - {lastItem} of {transactionsTotal}</p>;
  };
  
  const paginationSummaryMobile = () => {
    return <p className={classes.paginationSummaryMobile}>
      Fetched <b>{lastItem}</b> of <b>{transactionsTotal}</b> transactions
    </p>;
  };
  
  const isNextPageLoaded = () => {
    return getLastItem(currentPageId, pageSize, transactionsTotal) <= transactions?.length;
  };
  
  const loadPrevPage = () => {
    return setCurrentPageId(prevState => prevState - 1);
  };
  
  const loadNextPage = () => {
    const { values, formikHelpers } = formValues;
    const nextPageIdx = currentPageId + 1;
    
    if (!currentPages.includes(nextPageIdx)) {
      setCurrentPages(current => [...current, nextPageIdx]);
      if (!currentPageIsLast) {
        setIsButtonDisabled(true);
        fetchTransactionReportsOffset(values, formikHelpers, setIsButtonDisabled, transactionsLength);
      }
    }
    return setCurrentPageId(prevState => prevState + 1);
  };
  
  const CustomPagination = () => {
    return <>
      <RenderBreakpoint desktop>
        <Box display={'flex'} flexDirection={'row'} justifyContent={'center'}
             alignItems={'center'}>
          {isNextPageLoaded() && paginationSummaryDesktop()}
          <button disabled={currentPageId === 0} onClick={() => loadPrevPage()} className={classes.paginationBtn}>
            <ChevronLeft />
          </button>
  
          <button disabled={isButtonDisabled || currentPageIsLast} onClick={() => loadNextPage()}
                  className={classes.paginationBtn}>
            <ChevronRight />
          </button>
        </Box>
      </RenderBreakpoint>
  
      <RenderBreakpoint mobile>
        <Box className={classes.paginationMobile} display={'flex'} flexDirection={'row'} justifyContent={'center'}
             alignItems={'center'}>
          {paginationSummaryMobile()}
          <button disabled={isButtonDisabled || currentPageIsLast} onClick={() => loadNextPage()}
                  className={classes.paginationBtnMobile}>
            <span>FETCH</span>
          </button>
        </Box>
      </RenderBreakpoint>
    </>;
  };
  
  return (
    <CommonContainer hAlign="center">
      <h1 className={classes.transactionsHeader}>
        <FormattedMessage
          id="menu.main.transactions"
          defaultMessage="Transactions"
        />
      </h1>
  
      <TransactionsForm
        machinesOptions={machinesOptions}
        fetchTransactionReports={fetchTransactionReports}
        setFormValues={setFormValues}
        setCurrentPageId={setCurrentPageId}
      />
  
      {transactionsLength > 0 && <RegularDataGrid
        reportName={'Transactions'}
        columns={headers}
        rows={getTableData()}
        page={currentPageId}
        id={openItem[0]}
        setDownloadProgress={setDownloadProgress}
        components={{
          Toolbar: DefaultToolbar,
          Pagination: CustomPagination,
        }}
        handleExtraFunction={() => loadNextPage()}
        isAccordion={false}
        includeSearch={true}
      />}
  
      <LoadingSnackbar
        downloadFile={true}
        progress={downloadProgress}
        label={<FormattedMessage id="loading" defaultMessage="Loading" />}
      />
  
      <CommonSnackbar
        title={<FormattedMessage id="notification" defaultMessage="Notification" />}
        message={<FormattedMessage id="max.data"
                                   defaultMessage="The difference between start and end dates cannot exceed 7 days." />}
        open={!transactions}
      />
  
      {!!transactions && transactionsLength === 0 && <div className={classes.noDataFound}>
        <NoDataAvailableWithIcon />
      </div>}
    </CommonContainer>
  );
};

Transactions.propTypes = {
  machines: PropTypes.array,
  fetchTransactionReports: PropTypes.func,
  fetchTransactionReportsOffset: PropTypes.func,
  transactions: PropTypes.array,
};

const decorate = compose(
  connect(
    store => ({
      machines: store.user.machines,
      transactions: store.transactions.transactions,
      transactionsTotal: store.transactions.transactionsTotal,
    }),
    dispatch => bindActionCreators({
      fetchTransactionReports,
      fetchTransactionReportsOffset,
      downloadTransaction,
      clearTransactionReports,
    }, dispatch)),
);

export default decorate(Transactions);



